import {
  ADDITIONAL_SUBMISSION_PROPS,
  META_QUESTION_TYPES,
  MiscRowTypeName,
  QuestionTypeName,
  createEnum,
} from '#/constants'
import type { AnyRowTypeName } from '#/constants'

export const SUBMISSION_ACTIONS_ID = '__SubmissionActions'

export const VALIDATION_STATUS_ID_PROP = '_validation_status.uid'

// Columns that will be ALWAYS excluded from the view
export const EXCLUDED_COLUMNS = [
  '_xform_id_string',
  '_attachments',
  '_bamboo_dataset_id',
  // '_status' is always 'submitted_via_web' unless submitted in bulk
  // in that case, it's 'zip'
  '_status',
  'formhub/uuid',
  '_geolocation',
  'meta/instanceID',
  'meta/deprecatedID',
  // Internal only: the list of form versions a submission has been through
  'meta/formVersions',
  '_validation_status',
]

/**
 * These columns go at the very end of the list of columns, in this exact order.
 * They hold metadata that is of lesser interest to users than the responses to
 * the form questions.
 *
 * NOTE: this is part of the single source of truth for the order of columns,
 * see `orderColumns` in `tableUtils.ts`. Anything listed here is also treated
 * as metadata by `getMetadataColumns`, so don't add form questions.
 */
export const LAST_COLUMNS_ORDER: string[] = [
  META_QUESTION_TYPES.username,
  META_QUESTION_TYPES.deviceid,
  META_QUESTION_TYPES.phonenumber,
  META_QUESTION_TYPES.today,
  // Both the current and the legacy name of the form version column
  '__version__',
  '_version_',
  ADDITIONAL_SUBMISSION_PROPS._id,
  ADDITIONAL_SUBMISSION_PROPS._uuid,
  ADDITIONAL_SUBMISSION_PROPS._submission_time,
  ADDITIONAL_SUBMISSION_PROPS._submitted_by,
  ADDITIONAL_SUBMISSION_PROPS['meta/rootUuid'],
]

/**
 * Human friendly labels for the metadata columns (i.e. the columns that are not
 * responses to the form questions, so they have no label in the form
 * definition). Anything not listed here falls back to displaying the column key.
 */
export const METADATA_COLUMN_LABELS: { [key: string]: string } = {
  [META_QUESTION_TYPES.start]: t('start'),
  [META_QUESTION_TYPES.end]: t('end'),
  [META_QUESTION_TYPES.today]: t('today'),
  [META_QUESTION_TYPES.username]: t('username'),
  [META_QUESTION_TYPES.deviceid]: t('device ID'),
  [META_QUESTION_TYPES.phonenumber]: t('phone number'),
  [META_QUESTION_TYPES.audit]: t('audit'),
  [ADDITIONAL_SUBMISSION_PROPS._submitted_by]: t('Submitted by'),
  [ADDITIONAL_SUBMISSION_PROPS['meta/rootUuid']]: t('rootUuid'),
}

export enum SortValues {
  ASCENDING = 'ASCENDING',
  DESCENDING = 'DESCENDING',
}

// This is the setting object name from `asset.settings`
export const DATA_TABLE_SETTING = 'data-table'
// These are all possible settings of the above
export const DATA_TABLE_SETTINGS = Object.freeze({
  SELECTED_COLUMNS: 'selected-columns',
  FROZEN_COLUMN: 'frozen-column',
  SHOW_GROUP: 'show-group-name',
  TRANSLATION: 'translation-index',
  SHOW_HXL: 'show-hxl-tags',
  SORT_BY: 'sort-by',
})

export const TABLE_MEDIA_TYPES = createEnum([
  QuestionTypeName.file,
  QuestionTypeName.image,
  QuestionTypeName.audio,
  QuestionTypeName.video,
  QuestionTypeName['background-audio'],
])

export const DEFAULT_DATA_CELL_WIDTH = 140

export const CELLS_WIDTH_OVERRIDES: { [key: string]: number } = {}
CELLS_WIDTH_OVERRIDES[VALIDATION_STATUS_ID_PROP] = 125
CELLS_WIDTH_OVERRIDES[META_QUESTION_TYPES.start] = 110
CELLS_WIDTH_OVERRIDES[META_QUESTION_TYPES.end] = 110
CELLS_WIDTH_OVERRIDES[ADDITIONAL_SUBMISSION_PROPS._id] = 100
CELLS_WIDTH_OVERRIDES[QuestionTypeName.image] = 110
CELLS_WIDTH_OVERRIDES[QuestionTypeName.audio] = 170
CELLS_WIDTH_OVERRIDES[QuestionTypeName.video] = 110
CELLS_WIDTH_OVERRIDES[QuestionTypeName['background-audio']] = 170
Object.freeze(CELLS_WIDTH_OVERRIDES)

/**
 * For these question types the UI will display a dropdown filter in Data Table
 * for the matching column.
 */
export const DROPDOWN_FILTER_QUESTION_TYPES: AnyRowTypeName[] = [
  QuestionTypeName.select_multiple,
  QuestionTypeName.select_one,
]

/**
 * For these question types the UI will display a text filter in Data Table for
 * the matching column.
 */
export const TEXT_FILTER_QUESTION_TYPES: AnyRowTypeName[] = [
  QuestionTypeName.barcode,
  QuestionTypeName.calculate,
  QuestionTypeName.date,
  QuestionTypeName.datetime,
  QuestionTypeName.decimal,
  QuestionTypeName.hidden,
  QuestionTypeName.integer,
  QuestionTypeName.range,
  QuestionTypeName.rank,
  QuestionTypeName.score,
  // TODO: for now there is no code in `table.js` that makes the choices from
  // file available there, so we fallback to text filter
  QuestionTypeName.select_multiple_from_file,
  QuestionTypeName.select_one_from_file,
  // ENDTODO
  QuestionTypeName.text,
  QuestionTypeName.time,
  META_QUESTION_TYPES.start,
  META_QUESTION_TYPES.end,
  META_QUESTION_TYPES.username,
  META_QUESTION_TYPES.deviceid,
  META_QUESTION_TYPES.phonenumber,
  META_QUESTION_TYPES.today,
  MiscRowTypeName.score__row,
  MiscRowTypeName.rank__level,
]

/**
 * For these question ids the UI will display a text filter in Data Table for
 * the matching column. We need this, because these are additional submission
 * properties, so they don't have a question type attached to them.
 */
export const TEXT_FILTER_QUESTION_IDS = [
  '__version__',
  ADDITIONAL_SUBMISSION_PROPS._id,
  ADDITIONAL_SUBMISSION_PROPS._uuid,
  ADDITIONAL_SUBMISSION_PROPS._submission_time,
  ADDITIONAL_SUBMISSION_PROPS._submitted_by,
]

/**
 * These are question types that will be filtered by the exact filter value
 * (i.e. filter value is exactly the response). Any question type not on this
 * list will be filtered by responses that include the value (i.e. filter value
 * is part of the response).
 *
 * Every type that is not listed here is using "inexact" or "partial" match.
 */
export const FILTER_EXACT_TYPES: AnyRowTypeName[] = [
  QuestionTypeName.decimal,
  QuestionTypeName.integer,
  QuestionTypeName.range,
  QuestionTypeName.rank,
  QuestionTypeName.score,
  QuestionTypeName.select_one,
  QuestionTypeName.select_one_from_file,
]
